"use client";

import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronUp, ChevronDown, Eye, Pencil, Trash2, Search } from "lucide-react";

/**
 * Resource list payload from agent
 */
export interface ResourceListPayload {
  type: "resource_list";
  resources: ResourceItem[];
  columns?: string[];
  metadata?: {
    total_count?: number;
    grouped_by_type?: Record<string, number>;
    page?: number;
    page_size?: number;
  };
}

interface ResourceItem {
  source_id: string;
  source_name: string;
  source_type: string;
  connection_type: string;
  scope?: string;
  available?: boolean;
  created_at?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Extract JSON object from content that may have surrounding text
 */
function extractJson(content: string): string | null {
  const trimmed = content.trim();

  // Case 1: Pure JSON
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    return trimmed;
  }

  // Case 2: Markdown code block
  const codeBlockMatch = trimmed.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
  if (codeBlockMatch) {
    return codeBlockMatch[1];
  }

  // Case 3: JSON embedded in text
  const jsonMatch = trimmed.match(/(\{"type"\s*:\s*"resource_list"[\s\S]*\})/);
  if (jsonMatch) {
    const start = trimmed.indexOf(jsonMatch[1]);
    if (start >= 0) {
      let depth = 0;
      let end = start;
      for (let i = start; i < trimmed.length; i++) {
        if (trimmed[i] === '{') depth++;
        else if (trimmed[i] === '}') {
          depth--;
          if (depth === 0) {
            end = i + 1;
            break;
          }
        }
      }
      if (depth === 0) {
        return trimmed.slice(start, end);
      }
    }
  }

  return null;
}

/**
 * Check if content is a resource list message
 */
export function isResourceListMessage(content: string): ResourceListPayload | null {
  try {
    const jsonStr = extractJson(content);
    if (!jsonStr) return null;

    const parsed = JSON.parse(jsonStr);

    if (parsed.type === "resource_list" && Array.isArray(parsed.resources)) {
      return parsed as ResourceListPayload;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Column configuration
 */
interface ColumnDef {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  render?: (value: unknown, item: ResourceItem) => React.ReactNode;
}

const DEFAULT_COLUMNS: ColumnDef[] = [
  { key: "source_name", label: "Name", sortable: true },
  { key: "source_type", label: "Type", sortable: true },
  {
    key: "connection_type",
    label: "Connection",
    sortable: true,
    render: (value) => {
      const parts = String(value).split(".");
      return parts[parts.length - 1];
    }
  },
  { key: "scope", label: "Scope", sortable: true },
  {
    key: "created_at",
    label: "Created",
    sortable: true,
    render: (value) => {
      if (!value) return "-";
      const date = new Date(String(value));
      return date.toLocaleDateString();
    }
  },
];

/**
 * Props for ResourceListMessage
 */
interface ResourceListMessageProps {
  payload: ResourceListPayload;
  onView?: (resourceId: string) => void;
  onEdit?: (resourceId: string) => void;
  onDelete?: (resourceId: string) => void;
}

/**
 * ResourceListMessage - Renders a DataTable for resource lists
 */
export function ResourceListMessage({
  payload,
  onView,
  onEdit,
  onDelete
}: ResourceListMessageProps) {
  const [sortColumn, setSortColumn] = useState<string>("source_name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  // Get unique types for filter dropdown
  const uniqueTypes = useMemo(() => {
    const types = new Set<string>();
    payload.resources.forEach((r) => types.add(r.source_type));
    return Array.from(types).sort();
  }, [payload.resources]);

  // Filter and sort resources
  const filteredResources = useMemo(() => {
    let result = [...payload.resources];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          r.source_name.toLowerCase().includes(term) ||
          r.source_type.toLowerCase().includes(term) ||
          r.connection_type.toLowerCase().includes(term)
      );
    }

    // Apply type filter
    if (typeFilter) {
      result = result.filter((r) => r.source_type === typeFilter);
    }

    // Sort
    result.sort((a, b) => {
      const aVal = String(a[sortColumn] || "");
      const bVal = String(b[sortColumn] || "");
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === "asc" ? cmp : -cmp;
    });

    return result;
  }, [payload.resources, searchTerm, typeFilter, sortColumn, sortDirection]);

  // Handle sort click
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="h-3 w-3 inline ml-1" />
    ) : (
      <ChevronDown className="h-3 w-3 inline ml-1" />
    );
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Header with stats */}
      <div className="border-b border-border bg-muted/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Resources ({payload.metadata?.total_count || payload.resources.length})
            </h3>
            {payload.metadata?.grouped_by_type && (
              <div className="flex gap-2 mt-1">
                {Object.entries(payload.metadata.grouped_by_type).map(([type, count]) => (
                  <span
                    key={type}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground"
                  >
                    {type}: {count}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-border bg-muted/30 px-4 py-2 flex items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-muted border border-border rounded pl-7 pr-3 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-emerald-600"
          />
        </div>

        {/* Type filter */}
        <select
          value={typeFilter || ""}
          onChange={(e) => setTypeFilter(e.target.value || null)}
          className="bg-muted border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:border-emerald-600"
        >
          <option value="">All Types</option>
          {uniqueTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        {/* Results count */}
        <span className="text-[10px] text-muted-foreground">
          Showing {filteredResources.length} of {payload.resources.length}
        </span>
      </div>

      {/* Table */}
      <div className="max-h-[400px] overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-muted">
            <TableRow>
              {DEFAULT_COLUMNS.map((col) => (
                <TableHead
                  key={col.key}
                  className={col.sortable ? "cursor-pointer select-none" : ""}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  {col.label}
                  {col.sortable && renderSortIndicator(col.key)}
                </TableHead>
              ))}
              {(onView || onEdit || onDelete) && (
                <TableHead className="text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredResources.length > 0 ? (
              filteredResources.map((resource) => (
                <TableRow key={resource.source_id}>
                  {DEFAULT_COLUMNS.map((col) => (
                    <TableCell key={col.key} className="text-foreground text-xs">
                      {col.render
                        ? col.render(resource[col.key], resource)
                        : String(resource[col.key] || "-")}
                    </TableCell>
                  ))}
                  {(onView || onEdit || onDelete) && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {onView && (
                          <button
                            onClick={() => onView(resource.source_id)}
                            className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                            title="View details"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => onEdit(resource.source_id)}
                            className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(resource.source_id)}
                            className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={DEFAULT_COLUMNS.length + 1}
                  className="text-center text-muted-foreground py-8"
                >
                  No resources found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/**
 * ResourceListWrapper - Used in message rendering to detect and render lists
 */
interface ResourceListWrapperProps {
  text: string;
  onView?: (resourceId: string) => void;
  onEdit?: (resourceId: string) => void;
  onDelete?: (resourceId: string) => void;
}

export function ResourceListWrapper({ text, onView, onEdit, onDelete }: ResourceListWrapperProps) {
  const listPayload = isResourceListMessage(text);

  if (listPayload) {
    return (
      <ResourceListMessage
        payload={listPayload}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  }

  return null;
}
